import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hospital, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import {
  useSorSearch,
  type SorLookupReceives,
  type SorOrganisation,
} from '@/hooks/useSorLookup';
import {
  PARTY_MAX,
  isPickerIdentifierCode,
  type PickerIdentifierCode,
} from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type PickedReceiver = {
  organisation: SorOrganisation;
  identifier: string;
  chosenIdentifierCode: PickerIdentifierCode | null;
};

function IdentifierCodeSelect({
  id,
  value,
  onChange,
  missing,
  partyLabel,
  locationNumberFallback,
}: {
  id: string;
  value: PickerIdentifierCode | null;
  onChange: (code: PickerIdentifierCode | null) => void;
  missing: boolean;
  partyLabel: string;
  locationNumberFallback: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <Select
        id={id}
        label={t('patients.hospital_referral.identifier_code', 'Identifikatorkode')}
        value={value ?? ''}
        onChange={(event) => {
          const next = event.target.value;
          onChange(isPickerIdentifierCode(next) && (next !== 'lokationsnummer' || locationNumberFallback) ? next : null);
        }}
        aria-invalid={missing}
        className={missing ? 'border-[1.5px] border-ds-red' : undefined}
      >
        <option value="">{t('patients.hospital_referral.identifier_code_choose', 'Vælg identifikatorkode')}</option>
        {locationNumberFallback ? (
          <option value="lokationsnummer">
            {t('patients.hospital_referral.identifier_code_location', 'Lokationsnummer')}
          </option>
        ) : null}
        <option value="sygehusafdelingsnummer">
          {t('patients.hospital_referral.identifier_code_shak', 'Sygehusafdelingsnummer')}
        </option>
        <option value="ydernummer">{t('patients.hospital_referral.identifier_code_yder', 'Ydernummer')}</option>
      </Select>
      <p className={cn('mt-1', FORM_HINT_TYPE)}>
        {locationNumberFallback
          ? t(
              'patients.hospital_referral.identifier_location_hint',
              'Det nationale register har ingen identifikator for denne enhed. Lokationsnummeret (EAN) bruges, medmindre du vælger en anden kode.',
            )
          : t(
              'patients.hospital_referral.identifier_manual_hint',
              'Det nationale register har ingen identifikator for denne enhed. Den skal indtastes manuelt.',
            )}
      </p>
      {missing ? (
        <p role="alert" className={cn('mt-1', FORM_ERROR_TYPE)}>
          {t(
            'patients.hospital_referral.identifier_code_missing',
            '{{label}}: Identifikatorkoden mangler.',
            { label: partyLabel },
          )}
        </p>
      ) : null}
    </div>
  );
}

export function SorPicker({
  label,
  partyLabel,
  fieldId,
  selected,
  onSelect,
  onIdentifier,
  onIdentifierCode,
  onClear,
  optional = false,
  receives,
  disabledReason,
  hide,
  deliveryNote,
  locationNumberFallback = false,
}: {
  label: string;
  partyLabel: string;
  fieldId: string;
  selected: PickedReceiver | null;
  onSelect: (organisation: SorOrganisation) => void;
  onIdentifier: (identifier: string) => void;
  onIdentifierCode: (code: PickerIdentifierCode | null) => void;
  onClear: () => void;
  optional?: boolean;
  /** null: no SOR receive-type filter (the epikrise copy receiver); narrow it with `hide`. */
  receives: SorLookupReceives | readonly SorLookupReceives[] | null;
  /** A reason this organisation cannot be chosen right now. It is listed but not selectable. */
  disabledReason?: (organisation: SorOrganisation) => string | null;
  /** True when this organisation must not appear in the current search. */
  hide?: (organisation: SorOrganisation) => boolean;
  /** A note shown with an organisation, for example how the letter reaches it. */
  deliveryNote?: (organisation: SorOrganisation) => string | null;
  /**
   * Korrespondance (XDIS91 + XBIN01) only: offer `lokationsnummer`, and treat a unit without
   * a SOR identifier as complete, sent with the EAN as lokationsnummer. Off for XREF01/02/06.
   */
  locationNumberFallback?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const search = useSorSearch(selected ? '' : query, receives);
  const listed = search.data?.filter((org) => !hide?.(org));

  if (selected) {
    const org = selected.organisation;
    const addressLine = [org.address.street, org.address.postCode, org.address.district]
      .filter((part): part is string => Boolean(part))
      .join(', ');
    const empty = t('patients.hospital_referral.empty', 'Ikke udfyldt');
    const codeLabel = identifierCodeLabel(t, org.identifierCode ?? selected.chosenIdentifierCode);
    return (
      <div className="hv-pick">
        <div className="ph">
          <span className="ic">
            <Hospital className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <b>{org.name}</b>
            <span>
              EAN {org.ean || empty}
              {codeLabel ? ` · ${codeLabel}` : ''}
            </span>
          </div>
          <Button type="button" variant="ghost" size="sm" className="btn" onClick={onClear}>
            {optional
              ? t('patients.hospital_referral.copy_clear', 'Fjern kopimodtager')
              : t('patients.hospital_referral.receiver_change', 'Vælg en anden modtager')}
          </Button>
        </div>
        <dl className="hv-kv">
          <Kv label={t('patients.hospital_referral.organisation', 'Organisation')} value={org.name} empty={empty} />
          <Kv
            label={t('patients.hospital_referral.receiver_speciality', 'Speciale')}
            value={org.speciality}
            empty={empty}
          />
          <Kv label={t('patients.hospital_referral.ean', 'EAN')} value={org.ean} empty={empty} />
          <Kv label={t('patients.hospital_referral.street', 'Adresse')} value={addressLine} empty={empty} />
          <Kv
            label={t('patients.hospital_referral.identifier_code', 'Identifikatorkode')}
            value={codeLabel}
            empty={empty}
          />
        </dl>
        {deliveryNote?.(org) ? <p className={FORM_HINT_TYPE}>{deliveryNote(org)}</p> : null}
        {!org.identifierCode && (
          <IdentifierCodeSelect
            id={`${fieldId}-identifier-code`}
            value={selected.chosenIdentifierCode}
            onChange={onIdentifierCode}
            missing={!locationNumberFallback && selected.chosenIdentifierCode === null}
            partyLabel={partyLabel}
            locationNumberFallback={locationNumberFallback}
          />
        )}
        <div className="id">
          <Input
            label={t('patients.hospital_referral.identifier', 'Identifikator')}
            value={selected.identifier}
            onChange={(event) => onIdentifier(event.target.value)}
            error={
              selected.identifier.length > PARTY_MAX.identifier
                ? t(
                    'patients.hospital_referral.too_long',
                    '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
                    {
                      field: t('patients.hospital_referral.identifier', 'Identifikator'),
                      length: selected.identifier.length,
                      max: PARTY_MAX.identifier,
                    },
                  )
                : !locationNumberFallback && !org.identifierCode && selected.identifier.trim().length === 0
                  ? t(
                      'patients.hospital_referral.identifier_missing',
                      '{{label}}: Identifikatoren mangler.',
                      { label: partyLabel },
                    )
                  : undefined
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Input
        label={label}
        value={query}
        autoComplete="off"
        leadingIcon={<Search className="h-4 w-4" aria-hidden />}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
      />
      {search.isLoading && (
        <p className={cn('mt-2', FORM_HINT_TYPE)}>{t('patients.hospital_referral.searching', 'Søger…')}</p>
      )}
      {search.error ? (
        <p className={cn('mt-2', FORM_ERROR_TYPE)}>
          {t('patients.hospital_referral.search_failed', 'Søgningen fejlede.')}
        </p>
      ) : null}
      {listed && listed.length === 0 && (
        <p className={cn('mt-2', FORM_HINT_TYPE)}>
          {t('patients.hospital_referral.no_results', 'Ingen resultater')}
        </p>
      )}
      {listed && listed.length > 0 && (
        <ul className="mt-2 max-h-60 space-y-1 overflow-auto">
          {listed.map((org) => {
            const blockedReason = disabledReason?.(org) ?? null;
            const note = deliveryNote?.(org) ?? null;
            return (
              <li key={org.sorId}>
                <button
                  type="button"
                  disabled={blockedReason !== null}
                  className="w-full rounded-ctl border border-ds-line px-s3 py-s3 text-left hover:bg-ds-sunk disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                  onClick={() => onSelect(org)}
                >
                  <span className={cn('block', TYPE.body, 'text-ds-text')}>{org.name}</span>
                  <span className={cn('block', UI_MUTED)}>
                    {[org.speciality, org.ean, org.address.postCode, org.address.district]
                      .filter((part): part is string => Boolean(part))
                      .join(' · ')}
                  </span>
                  {note ? <span className={cn('block', UI_MUTED)}>{note}</span> : null}
                  {blockedReason ? <span className={cn('block', FORM_ERROR_TYPE)}>{blockedReason}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Kv({ label, value, empty }: { label: string; value?: string | null; empty: string }) {
  const missing = !value;
  return (
    <div>
      <dt>{label}</dt>
      <dd className={missing ? 'muted' : undefined}>{missing ? empty : value}</dd>
    </div>
  );
}

function identifierCodeLabel(
  t: (key: string, fallback: string) => string,
  code: string | null,
): string {
  switch (code) {
    case 'sygehusafdelingsnummer':
      return t('patients.hospital_referral.identifier_code_shak', 'Sygehusafdelingsnummer');
    case 'ydernummer':
      return t('patients.hospital_referral.identifier_code_yder', 'Ydernummer');
    case 'lokationsnummer':
      return t('patients.hospital_referral.identifier_code_location', 'Lokationsnummer');
    case null:
      return '';
    default:
      return code;
  }
}
